import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Компонент для обрезки длинного текста с подсказкой
function TruncatedText({ text, maxLength = 20 }) {
  if (!text || text.length <= maxLength) {
    return <>{text}</>;
  }
  
  return (
    <span className="truncated-text-container">
      <span className="truncated-text">{text.substring(0, maxLength)}...</span>
      <span className="truncated-tooltip">{text}</span>
    </span>
  );
}

const FinancePage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showUserStats, setShowUserStats] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userStats, setUserStats] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Функция для добавления уведомления
  const addNotification = (type, title, message) => {
    const id = Date.now() + Math.random();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    // Автоматически удаляем уведомление через 5 секунд
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  // Функция для удаления уведомления
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const API_URL = process.env.REACT_APP_API_URL || 'https://b.supchats.com';

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/admin/users/finance`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        // Сортируем пользователей по ID и приводим числовые поля к числам
        const sortedUsers = response.data.users
          .map(user => ({
            ...user,
            total_paid: parseFloat(user.total_paid || 0),
            chatgpt_cost: parseFloat(user.chatgpt_cost || 0)
          }))
          .sort((a, b) => a.id - b.id);
        setUsers(sortedUsers);
      }
    } catch (error) {
      addNotification(
        'error',
        'Ошибка загрузки',
        'Не удалось загрузить данные пользователей'
      );
    } finally {
      setLoading(false);
    }
  };

  const updatePayment = async (userId, amount) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/admin/users/${userId}/payment`, {
        amount: parseFloat(amount) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      // Обновляем локальные данные и сортируем по ID
      const updatedUsers = users.map(user => 
        user.id === userId 
          ? { ...user, total_paid: parseFloat(amount) || 0 }
          : user
      ).sort((a, b) => a.id - b.id);
      setUsers(updatedUsers);
      
      // Показываем уведомление об успехе
      addNotification(
        'success',
        'Платеж обновлен',
        `Сумма платежа успешно обновлена: $${parseFloat(amount) || 0}`
      );
    } catch (error) {
      // Показываем уведомление об ошибке
      addNotification(
        'error',
        'Ошибка обновления',
        'Не удалось обновить платеж. Попробуйте еще раз.'
      );
    }
  };

  const calculateProfit = (user) => {
    const totalPaid = parseFloat(user.total_paid || 0);
    const chatgptCost = parseFloat(user.chatgpt_cost || 0);
    return totalPaid - chatgptCost;
  };

  // Функция для умного форматирования денежных сумм
  const formatMoney = (amount) => {
    const num = parseFloat(amount || 0);
    
    if (num === 0) return '$0.00';
    
    // Для очень малых сумм (меньше 0.01) показываем до 6 знаков
    if (Math.abs(num) < 0.01) {
      return `$${num.toFixed(6)}`;
    }
    
    // Для малых сумм (меньше 1) показываем до 4 знаков  
    if (Math.abs(num) < 1) {
      return `$${num.toFixed(4)}`;
    }
    
    // Для обычных сумм показываем 2 знака
    return `$${num.toFixed(2)}`;
  };

  // Функция для получения детальной статистики пользователя
  const fetchUserStats = async (userId) => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/admin/users/${userId}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        setUserStats(response.data.stats);
      }
    } catch (error) {
      console.error('Ошибка получения статистики пользователя:', error);
      addNotification(
        'error',
        'Ошибка загрузки',
        'Не удалось загрузить статистику пользователя'
      );
    }
  };

  // Обработчик выбора пользователя
  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    if (userId) {
      fetchUserStats(userId);
    } else {
      setUserStats(null);
    }
  };

  if (loading) {
    return <div className="admin-loading">Загрузка пользователей...</div>;
  }

  return (
    <div className="finance-page page-container">
      <div className="page-header-fixed">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div className="moderation-stats-compact">
            <div className="stat-item-compact">
              <span className="stat-label">Всего пользователей:</span>
              <span className="stat-value">{users.length}</span>
            </div>
            <div className="stat-item-compact">
              <span className="stat-label">Расходы:</span>
              <span className="stat-value" style={{ background: '#fff3cd', color: '#856404' }}>
                {formatMoney(users.reduce((sum, user) => sum + parseFloat(user.chatgpt_cost || 0), 0))}
              </span>
            </div>
            <div className="stat-item-compact">
              <span className="stat-label">Доход:</span>
              <span className="stat-value" style={{ background: '#d4edda', color: '#155724' }}>
                {formatMoney(users.reduce((sum, user) => sum + parseFloat(user.total_paid || 0), 0))}
              </span>
            </div>
            <div className="stat-item-compact">
              <span className="stat-label">Прибыль:</span>
              <span className={`stat-value ${users.reduce((sum, user) => sum + calculateProfit(user), 0) >= 0 ? '' : ''}`} style={{ 
                background: users.reduce((sum, user) => sum + calculateProfit(user), 0) >= 0 ? '#d1ecf1' : '#f8d7da',
                color: users.reduce((sum, user) => sum + calculateProfit(user), 0) >= 0 ? '#0c5460' : '#721c24'
              }}>
                {formatMoney(users.reduce((sum, user) => sum + calculateProfit(user), 0))}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowUserStats(true)}
            className="btn btn-info"
            style={{ padding: '8px 16px', fontSize: '14px', whiteSpace: 'nowrap' }}
          >
            👤 Статистика пользователя
          </button>
        </div>
      </div>

      <div className="page-content-scrollable">
        <div className="finance-table-container">
          <div className="table-wrapper">
            <table className="finance-table">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Email</th>
                  <th>Расходы ChatGPT</th>
                  <th>Оплачено</th>
                  <th>Прибыль/Убыток</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <strong>{user.name || 'Не указано'}</strong>
                      <small>ID: {user.id}</small>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td className="cost-cell">
                    <span className="amount expense">
                      {formatMoney(user.chatgpt_cost || 0)}
                    </span>
                  </td>
                  <td className="payment-cell">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={user.total_paid || 0}
                      onBlur={(e) => updatePayment(user.id, e.target.value)}
                      className="payment-input"
                      placeholder="0.00"
                    />
                  </td>
                  <td className="profit-cell">
                    <span className={`amount ${calculateProfit(user) >= 0 ? 'profit' : 'loss'}`}>
                      {formatMoney(calculateProfit(user))}
                    </span>
                  </td>
                  <td>
                    <button
                      className="refresh-btn"
                      onClick={() => fetchUsers()}
                      title="Обновить данные"
                    >
                      🔄
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Модальное окно для статистики пользователя */}
      {showUserStats && (
        <div className="modal-overlay" onClick={() => setShowUserStats(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>👤 Статистика пользователя</h3>
              <button onClick={() => setShowUserStats(false)} className="modal-close">×</button>
            </div>
            
            <div className="modal-body">
              {/* Выбор пользователя */}
              <div className="user-select-section">
                <label htmlFor="user-select" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Выберите пользователя:
                </label>
                <select 
                  id="user-select"
                  value={selectedUserId}
                  onChange={(e) => handleUserSelect(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    border: '1px solid #ddd', 
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginBottom: '20px'
                  }}
                >
                  <option value="">-- Выберите пользователя --</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.email} (ID: {user.id})
                    </option>
                  ))}
                </select>
              </div>

              {/* Статистика выбранного пользователя */}
              {selectedUserId && userStats ? (
                <div className="user-stats-section">
                  <h4>📊 Статистика пользователя: {users.find(u => u.id == selectedUserId)?.email}</h4>
                  
                  {/* Общая информация */}
                  <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div className="stat-card">
                      <label>Общие расходы:</label>
                      <span className="expense">{formatMoney(userStats.total_cost || 0)}</span>
                    </div>
                    <div className="stat-card">
                      <label>Всего запросов:</label>
                      <span>{userStats.total_requests || 0}</span>
                    </div>
                    <div className="stat-card">
                      <label>Платежей внесено:</label>
                      <span className="profit">{formatMoney(userStats.total_paid || 0)}</span>
                    </div>
                    <div className="stat-card">
                      <label>Прибыль/убыток:</label>
                      <span className={userStats.profit >= 0 ? 'profit' : 'loss'}>
                        {formatMoney(userStats.profit || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Статистика по ботам */}
                  {userStats.by_agents && userStats.by_agents.length > 0 && (
                    <div className="agents-stats">
                      <h5>🤖 Статистика по ботам:</h5>
                      <div className="stats-table">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f8f9fa' }}>
                              <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Бот</th>
                              <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Расходы</th>
                              <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Запросы</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userStats.by_agents.map((agent, index) => (
                              <tr key={index}>
                                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                  <strong>
                                    <TruncatedText text={agent.agent_name} maxLength={15} />
                                  </strong>
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #ddd' }} className="expense">
                                  {formatMoney(agent.total_cost)}
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                  {agent.requests_count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedUserId ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Загрузка статистики...
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Выберите пользователя для просмотра статистики
                </div>
              )}
            </div>
          </div>
                 </div>
       )}

       {/* Контейнер для всплывающих уведомлений */}
       <div className="notification-container">
         {notifications.map((notification) => (
           <div key={notification.id} className={`notification ${notification.type}`}>
             <div className="notification-icon">
               {notification.type === 'success' && '✅'}
               {notification.type === 'error' && '❌'}
               {notification.type === 'info' && 'ℹ️'}
               {notification.type === 'warning' && '⚠️'}
             </div>
             <div className="notification-content">
               <div className="notification-title">{notification.title}</div>
               <div className="notification-message">{notification.message}</div>
             </div>
             <button 
               className="notification-close"
               onClick={() => removeNotification(notification.id)}
             >
               ×
             </button>
           </div>
         ))}
       </div>
     </div>
   );
 };

export default FinancePage;

